import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUp, Bot, CalendarDays, Headset, Loader2, Minus, X } from "lucide-react";
import { LogoMark } from "./Logo";
import { WHATSAPP_URL } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const STORE_KEY = "aviokan-chat-v1";
const MAX_INPUT = 2000;

export const WhatsAppIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
  </svg>
);

type Msg = { role: "user" | "assistant"; text: string; escalate?: boolean; suggestions?: string[] };

const GREETING: Msg = {
  role: "assistant",
  text: "Hello — I'm the **Aviokan AI assistant**.\n\nI can help with our solution practices, the 140+ OEM ecosystem, industries, compliance insights, open roles and how to engage our team. What can I do for you?",
};

const CHIP_SETS: Array<[RegExp, string[]]> = [
  [/^\/solutions\/data-centre/, ["What does a Tier III data centre build include?", "How do you approach disaster recovery?", "Can you relocate our server room?"]],
  [/^\/solutions\/network/, ["Can you audit our campus network?", "Do you do Wi-Fi 6E surveys?", "Which switching OEMs do you support?"]],
  [/^\/solutions\/cybersecurity/, ["Can you help with DPDP Act compliance?", "How does a 24x7 SOC engagement work?", "Which security OEMs do you work with?"]],
  [/^\/solutions\/cloud/, ["How does a cloud migration start?", "Can you manage our Microsoft 365?", "What is device-as-a-service?"]],
  [/^\/solutions\/collaboration/, ["Can you build our boardroom AV?", "Do you integrate CCTV and access control?", "How does an IP-PBX migration work?"]],
  [/^\/solutions\/software/, ["How do software engagements start?", "Who owns the code and IP?", "Can you build an AI assistant for us?"]],
  [/^\/solutions\/managed/, ["What does AMC support include?", "How fast is your SLA response?", "Can you run our NOC?"]],
  [/^\/solutions/, ["What solution practices do you offer?", "Which OEMs do you work with?", "How do engagements start?"]],
  [/^\/about\/careers/, ["What roles are open right now?", "How do I apply for a position?", "What is your hiring process?"]],
  [/^\/about\/blog/, ["What's new on the blog?", "Any DPDP Act guidance?", "What compliance topics do you cover?"]],
  [/^\/partners/, ["Which OEMs do you work with?", "How many OEMs do you support?", "How do partnerships work?"]],
  [/^\/clients/, ["Can I see case studies?", "Which industries do you serve?", "How do engagements start?"]],
  [/^\/digital-engineering/, ["What engineering services do you offer?", "Which stacks do you build on?", "Who owns the code and IP?"]],
  [/^\/contact/, ["How fast do you respond?", "Can I book a consultation?", "Where are you located?"]],
];
const DEFAULT_CHIPS = ["What solutions do you offer?", "How do we start an engagement?", "Which OEMs do you work with?"];

function chipsFor(pathname: string): string[] {
  for (const [re, chips] of CHIP_SETS) if (re.test(pathname)) return chips;
  return DEFAULT_CHIPS;
}

/** Strips AI control markers from display text, incl. a half-streamed trailing marker. */
function hideMarkers(t: string): string {
  return t
    .replace(/\[\[ESCALATE\]\]/gi, "")
    .replace(/\[\[SUGGEST:[\s\S]*?\]\]/gi, "")
    .replace(/\[\[[^\]]*$/, "")
    .trimEnd();
}

function loadStored(): { sid: string; msgs: Msg[] } {
  const fresh = { sid: `web-${Math.random().toString(36).slice(2)}${Date.now()}`, msgs: [GREETING] };
  try {
    const raw = sessionStorage.getItem(STORE_KEY);
    if (!raw) return fresh;
    const parsed = JSON.parse(raw);
    const msgs = Array.isArray(parsed?.msgs) ? parsed.msgs.filter((m: Msg) => m?.text) : [];
    const sid = typeof parsed?.sid === "string" && parsed.sid ? parsed.sid : fresh.sid;
    return msgs.length ? { sid, msgs: msgs.slice(-30) } : fresh;
  } catch {
    return fresh;
  }
}

/* ---------- markdown-lite ---------- */

function inline(text: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g;
  let last = 0;
  let k = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) {
      parts.push(<strong key={k++} className="font-semibold text-white">{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith("`")) {
      parts.push(<code key={k++} className="font-tech text-[11.5px] bg-white/10 border border-white/10 rounded px-1 py-0.5">{tok.slice(1, -1)}</code>);
    } else {
      const lm = tok.match(/\[([^\]]+)\]\(([^)\s]+)\)/);
      if (lm) {
        const href = lm[2];
        if (href.startsWith("/")) {
          parts.push(<Link key={k++} to={href} className="text-cy underline decoration-cy/40 underline-offset-2 hover:text-white transition-colors">{lm[1]}</Link>);
        } else {
          parts.push(<a key={k++} href={href} target="_blank" rel="noopener noreferrer" className="text-cy underline decoration-cy/40 underline-offset-2 hover:text-white transition-colors">{lm[1]}</a>);
        }
      }
    }
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function MdLite({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  let bullets: string[] = [];
  let bk = 0;
  const flush = (key: string) => {
    if (!bullets.length) return;
    blocks.push(
      <ul key={key} className="space-y-1 my-1.5">
        {bullets.map((b, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-cy mt-px shrink-0">–</span>
            <span>{inline(b)}</span>
          </li>
        ))}
      </ul>
    );
    bullets = [];
  };
  text.split("\n").forEach((line, i) => {
    const t = line.trim();
    if (/^[-•]\s+/.test(t)) {
      bullets.push(t.replace(/^[-•]\s+/, ""));
      return;
    }
    flush(`ul-${bk++}-${i}`);
    if (!t) {
      blocks.push(<div key={i} className="h-2" />);
      return;
    }
    blocks.push(<p key={i}>{inline(t)}</p>);
  });
  flush(`ul-${bk++}-end`);
  return <div className="space-y-0.5">{blocks}</div>;
}

/** Hover pill that slides in to the left of a launcher button (desktop only). */
function LauncherLabel({ text }: { text: string }) {
  return (
    <span className="pointer-events-none hidden sm:block absolute right-full mr-3 top-1/2 -translate-y-1/2 translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-200 whitespace-nowrap bg-[#07152E] border border-white/10 text-white font-tech text-[10px] tracking-[0.18em] px-3.5 py-2 rounded-full shadow-xl">
      {text}
    </span>
  );
}

export default function FloatingHelp() {
  const [bootstrap] = useState(loadStored);
  const sessionId = useRef(bootstrap.sid);
  const [messages, setMessages] = useState<Msg[]>(bootstrap.msgs);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [open, setOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const chips = chipsFor(location.pathname);

  /* Persist the conversation for the browser session. */
  useEffect(() => {
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify({ sid: sessionId.current, msgs: messages.filter((m) => m.text).slice(-30) }));
    } catch {
      /* storage full/blocked — conversation still works in memory */
    }
  }, [messages]);

  /* Track the visual viewport so the mobile sheet survives the on-screen keyboard. */
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const on = () => document.documentElement.style.setProperty("--vvh", `${Math.round(vv.height)}px`);
    on();
    vv.addEventListener("resize", on);
    vv.addEventListener("scroll", on);
    return () => {
      vv.removeEventListener("resize", on);
      vv.removeEventListener("scroll", on);
    };
  }, []);

  /* Escape minimises the panel. */
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open]);

  /* Lock body scroll behind the mobile bottom sheet (re-evaluates if the
     viewport crosses the mobile breakpoint while the panel is open). */
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 639px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const on = () => setIsMobile(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  useEffect(() => {
    if (!open || !isMobile) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, isMobile]);


  /* Autofocus the composer on desktop only (mobile keyboards pop unprompted). */
  useEffect(() => {
    if (!open || window.matchMedia("(max-width: 639px)").matches) return;
    const t = setTimeout(() => inputRef.current?.focus(), 260);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming, open]);

  const minimize = () => setOpen(false);

  const closeAndClear = () => {
    setOpen(false);
    sessionId.current = `web-${Math.random().toString(36).slice(2)}${Date.now()}`;
    setMessages([GREETING]);
    try {
      sessionStorage.removeItem(STORE_KEY);
    } catch {
      /* ignore */
    }
  };

  const send = async (raw?: string) => {
    const text = (raw ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text, page: location.pathname }),
      });
      if (!res.body) throw new Error("Empty response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const line = buf.slice(0, idx).trim();
          buf = buf.slice(idx + 2);
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const j = JSON.parse(payload);
            if (j.meta) {
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                copy[copy.length - 1] = {
                  ...last,
                  text: typeof j.meta.text === "string" && j.meta.text ? j.meta.text : last.text,
                  escalate: !!j.meta.escalate,
                  suggestions: Array.isArray(j.meta.suggestions) ? j.meta.suggestions.slice(0, 3) : [],
                };
                return copy;
              });
            }
            if (j.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], text: copy[copy.length - 1].text + j.delta };
                return copy;
              });
            }
            if (j.error) throw new Error(j.error);
          } catch (e) {
            if (e instanceof SyntaxError) continue;
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { ...copy[copy.length - 1], text: "Sorry — something went wrong. Please try again or email connect@aviokan.com." };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], text: "Connection issue — please try again shortly, or email connect@aviokan.com." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const openMeeting = () => window.dispatchEvent(new CustomEvent("open-meeting"));

  return (
    <>
      {/* ---------- chat panel: mobile bottom sheet / desktop floating panel ---------- */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="chat-panel"
            initial={{ opacity: 0, y: 44, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 32, scale: 0.98 }}
            transition={{ type: "spring", damping: 26, stiffness: 280 }}
            className="fixed z-[80] bottom-0 inset-x-0 h-[calc(var(--vvh,100dvh)-16px)] sm:inset-auto sm:bottom-[104px] sm:right-6 sm:w-[400px] sm:h-[min(640px,calc(var(--vvh,100dvh)-140px))] flex flex-col overflow-hidden rounded-t-2xl sm:rounded-2xl border border-white/10 backdrop-blur-2xl shadow-[0_40px_90px_-20px_rgba(2,8,26,0.85)]"
            style={{
              backgroundColor: "rgba(10, 31, 68, 0.97)",
              backgroundImage: "radial-gradient(rgba(0,194,209,0.06) 1px, transparent 1px)",
              backgroundSize: "22px 22px",
            }}
            role="dialog"
            aria-label="Aviokan AI assistant chat"
            data-testid="chat-panel"
          >
            {/* header */}
            <div className="shrink-0 border-b border-white/10 bg-gradient-to-r from-[#07152E] via-[#0A1F44] to-[#0A1F44] px-4 sm:px-5 py-3.5">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 shrink-0 rounded-full bg-[#07152E] border border-cy/40 shadow-[0_0_18px_rgba(0,194,209,0.35)] grid place-items-center">
                  <LogoMark className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-white font-display font-bold text-[15px] leading-none">Aviokan AI</p>
                  <p className="text-cy font-tech text-[9.5px] tracking-[0.18em] mt-1.5 flex items-center gap-1.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-cy animate-pulse shrink-0" />
                    ONLINE · GROUNDED ON AVIOKAN.COM
                  </p>
                </div>
                <button
                  onClick={minimize}
                  aria-label="Minimise chat"
                  title="Minimise"
                  data-testid="chat-minimize-button"
                  className="h-8 w-8 grid place-items-center rounded-full text-white/60 hover:text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cy/70 transition-colors"
                >
                  <Minus size={16} />
                </button>
                <button
                  onClick={closeAndClear}
                  aria-label="Close chat and clear conversation"
                  title="Close and clear"
                  data-testid="chat-close-button"
                  className="h-8 w-8 grid place-items-center rounded-full text-white/60 hover:text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cy/70 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* messages */}
            <div ref={scrollRef} data-lenis-prevent className="flex-1 overflow-y-auto px-4 sm:px-5 py-5 space-y-4" aria-live="polite" aria-label="Conversation" data-testid="chat-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "gap-2.5"}`} data-testid={`chat-message-${m.role}-${i}`}>
                  {m.role === "assistant" && (
                    <div className="h-7 w-7 rounded-full bg-[#07152E] border border-cy/30 grid place-items-center shrink-0 mt-0.5">
                      <LogoMark className="h-4 w-4" />
                    </div>
                  )}
                  <div className={m.role === "user" ? "max-w-[88%]" : "max-w-[88%] min-w-0"}>
                    <div
                      className={`px-4 py-2.5 text-[13px] leading-relaxed whitespace-pre-wrap ${
                        m.role === "user"
                          ? "bg-[#FF5B1F]/15 border border-[#FF5B1F]/30 text-white rounded-2xl rounded-br-sm"
                          : "bg-white/[0.05] border border-white/10 text-[#F9FAFB] rounded-2xl rounded-tl-sm"
                      }`}
                    >
                      {m.text ? (
                        <MdLite text={hideMarkers(m.text)} />
                      ) : streaming && i === messages.length - 1 ? (
                        <span className="inline-flex gap-1 py-1">
                          <Dot delay="0s" />
                          <Dot delay="0.15s" />
                          <Dot delay="0.3s" />
                        </span>
                      ) : null}
                    </div>
                    {m.role === "assistant" && m.escalate && (
                      <button
                        onClick={openMeeting}
                        data-testid="chat-escalate-cta"
                        className="mt-2.5 inline-flex items-center gap-2 bg-ember text-white text-[12px] font-bold px-4 py-2.5 rounded-full hover:bg-[#E54D16] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cy/70 transition-colors shadow-[0_8px_24px_-6px_rgba(255,91,31,0.6)]"
                      >
                        <CalendarDays size={14} /> Talk to an expert
                      </button>
                    )}
                    {m.role === "assistant" && i === messages.length - 1 && !streaming && !!m.suggestions?.length && (
                      <div className="flex flex-wrap gap-2 mt-2.5">
                        {m.suggestions.map((s) => (
                          <button
                            key={s}
                            onClick={() => send(s)}
                            data-testid="chat-suggestion"
                            className="text-[12px] px-3.5 py-2 rounded-full border border-cy/30 text-cy hover:bg-cy/10 hover:border-cy/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cy/70 text-left transition-colors"
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* quick-start chips (fresh conversation only) */}
              {messages.length <= 1 && !streaming && (
                <div className="pl-10 flex flex-col items-start gap-2" data-testid="chat-quick-chips">
                  <p className="text-[9.5px] font-tech tracking-[0.22em] text-white/35">SUGGESTED FOR THIS PAGE</p>
                  {chips.map((c) => (
                    <button
                      key={c}
                      onClick={() => send(c)}
                      data-testid="chat-quick-chip"
                      className="text-[12px] px-4 py-2.5 rounded-full border border-cy/30 text-cy hover:bg-cy/10 hover:border-cy/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cy/70 text-left transition-colors"
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* composer */}
            <div className="shrink-0 border-t border-white/10 bg-[#07152E]/85 px-3 sm:px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))]">
              <div className="flex items-center gap-2 bg-white/[0.06] border border-white/15 rounded-full pl-4 pr-1.5 py-1.5 focus-within:border-cy/60 focus-within:shadow-[0_0_0_3px_rgba(0,194,209,0.15)] transition-colors">
                <input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  maxLength={MAX_INPUT}
                  placeholder="Ask about solutions, OEMs, careers…"
                  className="flex-1 bg-transparent text-sm text-white placeholder:text-white/35 outline-none min-w-0"
                  aria-label="Chat message"
                  data-testid="chat-input"
                />
                <button
                  onClick={() => send()}
                  disabled={streaming || !input.trim()}
                  aria-label="Send message"
                  data-testid="chat-send-button"
                  className="h-9 w-9 shrink-0 rounded-full bg-ember text-white grid place-items-center hover:bg-[#E54D16] disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cy/70 transition-colors"
                >
                  {streaming ? <Loader2 size={15} className="animate-spin" /> : <ArrowUp size={16} />}
                </button>
              </div>
              <p className="mt-2 text-center text-[9.5px] font-tech tracking-[0.14em] text-white/30">
                AI ANSWERS GROUND ON AVIOKAN.COM · VERIFY CRITICAL DETAILS WITH OUR TEAM
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---------- launcher stack ---------- */}
      <div
        className="fixed right-4 bottom-[max(1.25rem,env(safe-area-inset-bottom))] sm:right-6 sm:bottom-6 z-[75] flex flex-col items-end gap-2.5"
        data-testid="floating-help"
      >
        <AnimatePresence>
          {!open && (
            <motion.div
              key="secondary"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10, scale: 0.9 }}
              transition={{ duration: 0.18 }}
              className="flex flex-col items-end gap-2.5"
            >
              <div className="relative group">
                <LauncherLabel text="TALK TO AN EXPERT" />
                <Link
                  to="/contact"
                  aria-label="Talk to an expert"
                  data-testid="expert-float-button"
                  className="h-12 w-12 rounded-full grid place-items-center bg-[#0A1F44] text-cy border border-cy/40 shadow-[0_10px_30px_-8px_rgba(10,31,68,0.7)] hover:bg-cy hover:text-[#0A1F44] hover:shadow-[0_12px_34px_-8px_rgba(0,194,209,0.55)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cy/70 transition-all duration-200"
                >
                  <Headset size={19} />
                </Link>
              </div>
              <div className="relative group">
                <LauncherLabel text="WHATSAPP US" />
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Chat on WhatsApp"
                  data-testid="whatsapp-float-button"
                  className="h-12 w-12 rounded-full grid place-items-center bg-[#25D366] text-white shadow-[0_10px_30px_-8px_rgba(37,211,102,0.6)] hover:brightness-110 hover:shadow-[0_12px_34px_-8px_rgba(37,211,102,0.75)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cy/70 transition-all duration-200"
                >
                  <WhatsAppIcon size={19} />
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <button
          onClick={() => (open ? minimize() : setOpen(true))}
          aria-label={open ? "Minimise assistant" : "Open AI assistant"}
          data-testid="chat-float-button"
          className="relative h-14 w-14 rounded-full grid place-items-center bg-gradient-to-br from-[#FF6B2E] to-[#E5470F] text-white shadow-[0_14px_40px_-8px_rgba(255,91,31,0.65)] hover:brightness-110 hover:shadow-[0_16px_44px_-8px_rgba(255,91,31,0.8)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cy/70 transition-all duration-200"
        >
          {open ? <Minus size={20} /> : <Bot size={22} />}
          {!open && (
            <span className="absolute top-1 right-1 h-3 w-3">
              <span className="absolute inline-flex h-full w-full rounded-full bg-cy opacity-75 animate-ping" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-cy border-2 border-[#0A1F44]" />
            </span>
          )}
        </button>
      </div>
    </>
  );
}

function Dot({ delay }: { delay: string }) {
  return <span className="h-1.5 w-1.5 rounded-full bg-cy animate-bounce" style={{ animationDelay: delay, animationDuration: "0.9s" }} />;
}
